import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowUpDown, Lightbulb, Droplets, ShieldCheck, ShoppingBag, ClipboardCheck, UserPlus, Building2, MapPin, BadgeCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/common";
import { API } from "@/lib/api";

const OWNER_SERVICES = [
  { icon: ArrowUpDown, title: "صيانة المصاعد", img: "/images/s_elev.jpg" },
  { icon: Lightbulb, title: "إضاءة المناطق المشتركة", img: "/images/s_light.jpg" },
  { icon: Droplets, title: "السباكة والشبكة العامة", img: "/images/s_plumb.jpg" },
];

const STEPS = [
  { icon: UserPlus, title: "أنشئ حسابك", text: "سجّل ببريدك الإلكتروني وكلمة مرور." },
  { icon: Building2, title: "اختر مشروعك ووحدتك", text: "حدد مشروعك المتعاقد ورقم وحدتك السكنية." },
  { icon: BadgeCheck, title: "اعتماد الإدارة", text: "تراجع إدارة دارنا بياناتك وتفعّل حسابك." },
];

const Nav = () => (
  <header className="absolute inset-x-0 top-0 z-30">
    <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-5 sm:px-6">
      <Logo light />
      <div className="flex items-center gap-2">
        <Link to="/sign-in" data-testid="landing-login-button">
          <Button variant="ghost" className="rounded-full text-white hover:bg-white/10 hover:text-white">تسجيل الدخول</Button>
        </Link>
        <Link to="/sign-up" data-testid="landing-register-button-nav">
          <Button className="rounded-full bg-[#C59B27] text-[#0F382C] font-bold hover:bg-[#B0881E]">إنشاء حساب</Button>
        </Link>
      </div>
    </div>
  </header>
);

const Hero = () => (
  <section className="relative overflow-hidden rounded-b-[2.5rem] bg-[#0F382C] text-white">
    <img src="/images/hero.jpg" alt="مشروع سكني" className="absolute inset-0 h-full w-full object-cover opacity-30" />
    <div className="absolute inset-0 bg-gradient-to-l from-[#0F382C] via-[#0F382C]/85 to-transparent" />
    <Nav />
    <div className="relative mx-auto max-w-7xl px-4 pb-24 pt-36 sm:px-6 lg:pb-32 lg:pt-44">
      <div className="max-w-2xl fade-up">
        <span className="inline-flex items-center gap-2 rounded-full border border-[#C59B27]/40 bg-[#C59B27]/10 px-4 py-1.5 text-sm text-[#E8C766]">
          <ShieldCheck className="h-4 w-4" /> حصرياً لملاك المشاريع السكنية المتعاقدة
        </span>
        <h1 className="mt-6 text-4xl font-extrabold leading-tight sm:text-5xl lg:text-6xl">
          دارنا… <span className="text-[#C59B27]">عناية متكاملة</span> بمنزلك ومجمعك السكني
        </h1>
        <p className="mt-6 text-base leading-relaxed text-white/80 sm:text-lg">
          منصة واحدة لطلب صيانة المرافق المشتركة مجاناً ضمن عقد مشروعك، وتسوّق مستلزمات منزلك مع خدمة التركيب، وتتبع كل طلباتك لحظة بلحظة.
        </p>
        <div className="mt-10 flex flex-wrap gap-3">
          <Link to="/sign-up" data-testid="hero-register-button">
            <Button className="h-12 rounded-full bg-[#C59B27] px-8 text-base font-bold text-[#0F382C] hover:bg-[#B0881E]">سجّل كمالك</Button>
          </Link>
          <Link to="/sign-in" data-testid="hero-login-button">
            <Button variant="outline" className="h-12 rounded-full border-white/40 bg-transparent px-8 text-base text-white hover:bg-white/10 hover:text-white">لديّ حساب — دخول</Button>
          </Link>
        </div>
      </div>
    </div>
  </section>
);

const TwoSections = () => (
  <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6">
    <div className="grid gap-6 md:grid-cols-12">
      <div className="md:col-span-7 rounded-[2rem] bg-white p-8 sm:p-10 border border-[#E5DEC9]">
        <p className="text-sm font-bold text-[#C59B27]">القسم الأول</p>
        <h2 className="mt-2 text-2xl sm:text-3xl font-extrabold text-[#0F382C]">خدمات الملاك</h2>
        <p className="mt-3 text-[#5C7067]">بلاغات الصيانة المشتركة لمشروعك — مجانية ضمن العقد، مع تعيين فني ومتابعة الحالة.</p>
        <div className="mt-8 grid gap-4 sm:grid-cols-3">
          {OWNER_SERVICES.map((s) => (
            <div key={s.title} className="group overflow-hidden rounded-2xl border border-[#E5DEC9]">
              <img src={s.img} alt={s.title} className="h-28 w-full object-cover transition-transform duration-500 group-hover:scale-105" />
              <div className="flex items-center gap-2 p-3 text-sm font-bold text-[#0F382C]"><s.icon className="h-4 w-4 text-[#C59B27]" />{s.title}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="md:col-span-5 relative overflow-hidden rounded-[2rem] bg-[#0F382C] p-8 sm:p-10 text-white">
        <div className="absolute inset-0 pattern-bg opacity-40" />
        <div className="relative">
          <p className="text-sm font-bold text-[#C59B27]">القسم الثاني</p>
          <h2 className="mt-2 text-2xl sm:text-3xl font-extrabold">الخدمات الشخصية</h2>
          <p className="mt-3 text-white/75">متجر منزلي للمنتجات مع خيار التركيب: لمبات، صنابير، وخدمات سباكة داخل وحدتك.</p>
          <ul className="mt-8 space-y-3 text-sm">
            {["أسعار واضحة ورسوم تركيب منفصلة", "سلة مشتريات وطلب بخطوة واحدة", "الدفع عند التنفيذ", "تتبع حالة الطلب حتى التسليم"].map((t) => (
              <li key={t} className="flex items-center gap-2"><ShoppingBag className="h-4 w-4 text-[#C59B27]" />{t}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  </section>
);

const HowItWorks = () => (
  <section className="bg-[#F7F4EF] py-24">
    <div className="mx-auto max-w-7xl px-4 sm:px-6">
      <h2 className="text-2xl sm:text-3xl font-extrabold text-[#0F382C]">كيف تبدأ مع دارنا؟</h2>
      <div className="mt-10 grid gap-6 md:grid-cols-3">
        {STEPS.map((s, i) => (
          <div key={s.title} className="rounded-2xl bg-white p-8 border border-[#E5DEC9] fade-up" style={{ animationDelay: `${i * 120}ms` }}>
            <div className="flex items-center justify-between">
              <s.icon className="h-8 w-8 text-[#0F382C]" />
              <span className="text-5xl font-black text-[#E5DEC9]">{i + 1}</span>
            </div>
            <h3 className="mt-6 text-lg font-bold text-[#0F382C]">{s.title}</h3>
            <p className="mt-2 text-sm text-[#5C7067]">{s.text}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Projects = () => {
  const [projects, setProjects] = useState([]);
  useEffect(() => {
    axios.get(`${API}/public/projects`).then((r) => setProjects(r.data)).catch(() => setProjects([]));
  }, []);
  if (!projects.length) return null;
  return (
    <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6" data-testid="landing-projects">
      <h2 className="text-2xl sm:text-3xl font-extrabold text-[#0F382C]">المشاريع المتعاقدة</h2>
      <p className="mt-2 text-[#5C7067]">المنصة متاحة فقط لملاك المشاريع المتعاقدة والمفعّلة.</p>
      <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {projects.map((p) => (
          <div key={p.id} className="group overflow-hidden rounded-2xl bg-white border border-[#E5DEC9]" data-testid={`landing-project-${p.id}`}>
            <div className="overflow-hidden"><img src={p.image || "/images/p1.jpg"} alt={p.name} className="h-48 w-full object-cover transition-transform duration-500 group-hover:scale-105" /></div>
            <div className="p-6">
              <h3 className="font-bold text-[#0F382C]">{p.name}</h3>
              <p className="mt-1 flex items-center gap-1 text-sm text-[#5C7067]"><MapPin className="h-4 w-4" />{p.city}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

const Footer = () => (
  <footer className="bg-[#0F382C] text-white">
    <div className="mx-auto flex max-w-7xl flex-col gap-6 px-4 py-14 sm:flex-row sm:items-center sm:justify-between sm:px-6">
      <div>
        <Logo light />
        <p className="mt-3 text-sm text-white/60">خدمات الملاك والخدمات المنزلية للمشاريع السكنية في المملكة.</p>
      </div>
      <Link to="/sign-up" data-testid="footer-register-button">
        <Button className="rounded-full bg-[#C59B27] px-8 font-bold text-[#0F382C] hover:bg-[#B0881E]"><ClipboardCheck className="ml-2 h-4 w-4" />ابدأ التسجيل</Button>
      </Link>
    </div>
  </footer>
);

export default function Landing() {
  return (
    <div className="bg-[#FAF7F2]" data-testid="landing-page">
      <Hero />
      <TwoSections />
      <HowItWorks />
      <Projects />
      <Footer />
    </div>
  );
}
